import { useMemo, useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { SelectStyles } from '../../../../../../components/Select'
import { useThemeMode } from '../../../../../../_metronic/partials'
import type { CoverageDimensions } from '../model/coverage'
import type { RoutingContext, Direction, Source } from '../model/types'
import { encodeContextId } from '../model/context'
import {
  methodLabel,
  flagLabel,
  DIRECTION_LABEL,
  SOURCE_LABEL,
} from '../labels'

const AppSwal = withReactContent(Swal)

interface Option {
  value: string
  label: string
}

interface Props {
  show: boolean
  onClose: () => void
  dimensions: CoverageDimensions
  /** contextId set of all EXISTING configurations — for anti-dup detection. */
  existingContextIds: Set<string>
  /** Current merchant filter on the map (token) — used as merchant default. */
  merchantFilter: string | null
}

/** First option value or '' if list empty. */
function firstOr(opts: Option[], fallback = ''): string {
  return opts.length ? opts[0].value : fallback
}

/**
 * CHC-871: флоу «Новая конфигурация».
 *
 * Модалка с 6 селектами (по одному значению на ось — wildcard НЕ предусмотрен).
 * На «Создать» собирает RoutingContext, считает encodeContextId и навигирует в
 * редактор. Anti-dup: если конфиг с таким contextId уже существует — открывает
 * существующий вместо создания дубля. Сам редактор для нового контекста покажет
 * пустое состояние «Контекст не настроен» + «Заполнить из шаблона».
 */
export const NewConfigDialog = ({
  show,
  onClose,
  dimensions,
  existingContextIds,
  merchantFilter,
}: Props) => {
  const navigate = useNavigate()
  const { mode: theme } = useThemeMode()

  const merchantOptions = useMemo<Option[]>(
    () => dimensions.merchants.map((m) => ({ value: m.token, label: m.title })),
    [dimensions.merchants]
  )
  const methodOptions = useMemo<Option[]>(
    () => dimensions.paymentMethods.map((t) => ({ value: t, label: methodLabel(t) })),
    [dimensions.paymentMethods]
  )
  const currencyOptions = useMemo<Option[]>(
    () => dimensions.currencies.map((c) => ({ value: c, label: c })),
    [dimensions.currencies]
  )
  const directionOptions = useMemo<Option[]>(
    () => dimensions.directions.map((d) => ({ value: d, label: DIRECTION_LABEL[d] })),
    [dimensions.directions]
  )
  const flagOptions = useMemo<Option[]>(
    () => dimensions.externalUserFlags.map((f) => ({ value: f, label: flagLabel(f) })),
    [dimensions.externalUserFlags]
  )
  const sourceOptions = useMemo<Option[]>(
    () => dimensions.sources.map((s) => ({ value: s, label: SOURCE_LABEL[s] })),
    [dimensions.sources]
  )

  // Defaults: merchant = текущий фильтр (если выбран и валиден), иначе первый;
  // валюта = RUB если есть; флаг = ALL если есть; направление/метод/источник —
  // первый, кроме источника, где приоритет MERCHANT.
  const defaults = useMemo(() => {
    const merchant =
      merchantFilter && merchantOptions.some((o) => o.value === merchantFilter)
        ? merchantFilter
        : firstOr(merchantOptions)
    const currency = currencyOptions.some((o) => o.value === 'RUB')
      ? 'RUB'
      : firstOr(currencyOptions)
    const flag = flagOptions.some((o) => o.value === 'ALL') ? 'ALL' : firstOr(flagOptions)
    const source = sourceOptions.some((o) => o.value === 'MERCHANT')
      ? 'MERCHANT'
      : firstOr(sourceOptions)
    return {
      merchant,
      method: firstOr(methodOptions),
      currency,
      direction: firstOr(directionOptions),
      flag,
      source,
    }
  }, [
    merchantFilter,
    merchantOptions,
    methodOptions,
    currencyOptions,
    directionOptions,
    flagOptions,
    sourceOptions,
  ])

  const [merchant, setMerchant] = useState(defaults.merchant)
  const [method, setMethod] = useState(defaults.method)
  const [currency, setCurrency] = useState(defaults.currency)
  const [direction, setDirection] = useState(defaults.direction)
  const [flag, setFlag] = useState(defaults.flag)
  const [source, setSource] = useState(defaults.source)

  // Re-seed defaults each time the dialog opens (merchant filter may have changed).
  useEffect(() => {
    if (!show) return
    setMerchant(defaults.merchant)
    setMethod(defaults.method)
    setCurrency(defaults.currency)
    setDirection(defaults.direction)
    setFlag(defaults.flag)
    setSource(defaults.source)
  }, [show, defaults])

  const allChosen = Boolean(
    merchant && method && currency && direction && flag && source
  )

  const create = () => {
    if (!allChosen) return
    const ctx: RoutingContext = {
      merchantToken: merchant,
      paymentMethodType: method,
      currency,
      direction: direction as Direction,
      externalUserFlag: flag,
      source: source as Source,
    }
    const id = encodeContextId(ctx)
    const exists = existingContextIds.has(id)
    onClose()
    if (exists) {
      AppSwal.fire({
        title: 'Конфигурация уже существует — открываю',
        timer: 1500,
        showConfirmButton: false,
      })
    }
    navigate(`/settings/adapter-routing/config/${id}`)
  }

  const field = (
    label: string,
    options: Option[],
    value: string,
    onChange: (v: string) => void
  ) => (
    <div className='mb-5'>
      <label className='form-label fw-semibold fs-7 text-gray-700'>{label}</label>
      <Select
        options={options}
        // Modal clips overflow — render menu in a body portal so it isn't cut off.
        menuPortalTarget={document.body}
        styles={{
          ...SelectStyles(theme),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        value={options.find((o) => o.value === value) ?? null}
        onChange={(opt) => onChange((opt as Option | null)?.value ?? '')}
        placeholder='Выберите…'
      />
    </div>
  )

  return (
    <Modal show={show} onHide={onClose} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>Новая конфигурация маршрутизации</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='text-gray-600 fs-7 mb-5'>
          Выберите все 6 осей — по одному значению на каждую. После создания
          откроется редактор, где можно заполнить адаптеры из шаблона.
        </div>
        <div className='row'>
          <div className='col-md-6'>
            {field('Мерчант', merchantOptions, merchant, setMerchant)}
            {field('Метод', methodOptions, method, setMethod)}
            {field('Валюта', currencyOptions, currency, setCurrency)}
          </div>
          <div className='col-md-6'>
            {field('Направление', directionOptions, direction, setDirection)}
            {field('Клиентский флаг', flagOptions, flag, setFlag)}
            {field('Источник', sourceOptions, source, setSource)}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light' onClick={onClose}>
          Отмена
        </button>
        <button
          type='button'
          className='btn btn-primary'
          onClick={create}
          disabled={!allChosen}
        >
          Создать
        </button>
      </Modal.Footer>
    </Modal>
  )
}
